/** <scapescene-header>: the sticky app bar, shown on every screen.
 *
 * Left: the Scapescene brand (logo + wordmark, linking to /start), then —
 * once a group is active — the group chip (name + code) that toggles a
 * dropdown overlay listing memberships to switch between, plus a link
 * back to /start to join or create another group. Right: Setup / Frames /
 * Film nav links, highlighting the active route. */

import { store } from "../store";
import { router } from "../control/router";
import { SCAPESCENE_LOGO_SVG } from "./logo";
import {
  applyTheme,
  getLang,
  getTheme,
  setLang,
  setTheme,
  t,
} from "../lib/i18n";

class WorkshopHeader extends HTMLElement {
  #unsubscribeStore: (() => void) | null = null;
  #unsubscribeRouter: (() => void) | null = null;
  #built = false;
  #open = false;
  #onDocPointerDown = (event: Event) => this.#handleOutsideTap(event);

  connectedCallback(): void {
    this.#build();
    this.#update();
    this.#unsubscribeStore = store.onChange(() => this.#update());
    this.#unsubscribeRouter = router.onChange(() => this.#updateNav());
    this.getRootNode().addEventListener("pointerdown", this.#onDocPointerDown, true);
  }

  disconnectedCallback(): void {
    this.#unsubscribeStore?.();
    this.#unsubscribeStore = null;
    this.#unsubscribeRouter?.();
    this.#unsubscribeRouter = null;
    this.getRootNode().removeEventListener(
      "pointerdown",
      this.#onDocPointerDown,
      true,
    );
  }

  #build(): void {
    if (this.#built) return;
    this.#built = true;
    this.innerHTML = `
      <div class="scapescene-header">
        <a class="scapescene-header__brand" href="#/start">
          ${SCAPESCENE_LOGO_SVG}
          <span class="scapescene-header__wordmark">Starling Storyboard</span>
        </a>
        <button type="button" class="scapescene-header__chip">
          <span class="scapescene-header__chip-name"></span>
          <span class="scapescene-header__chip-code"></span>
        </button>
        <nav class="scapescene-header__nav">
          <a class="ss-btn ss-btn--sm" href="#/group" data-route="group">${t("navSetup")}</a>
          <a class="ss-btn ss-btn--sm" href="#/frame/1" data-route="frame">${t("navFrames")}</a>
          <a class="ss-btn ss-btn--sm" href="#/film" data-route="film">${t("navFilm")}</a>
        </nav>
        <button type="button" class="ss-btn ss-btn--sm scapescene-header__lang" aria-label="Taal / language">
          ${getLang() === "nl" ? "EN" : "NL"}
        </button>
        <button type="button" class="ss-btn ss-btn--sm scapescene-header__theme" aria-label="${t("themeToggle")}" title="${t("themeToggle")}">
          ${themeGlyph()}
        </button>
        <div class="scapescene-header__overlay" hidden>
          <div class="scapescene-header__memberships"></div>
          <div class="scapescene-header__divider"></div>
          <button type="button" class="ss-btn ss-btn--ghost scapescene-header__other">
            ${t("joinOrCreate")}
          </button>
        </div>
      </div>
    `;

    const chip = this.querySelector<HTMLButtonElement>(".scapescene-header__chip")!;
    chip.addEventListener("click", (event) => {
      event.stopPropagation();
      this.#toggle();
    });

    // Shows the language you would SWITCH TO, like airline sites do.
    const langBtn = this.querySelector<HTMLButtonElement>(
      ".scapescene-header__lang",
    )!;
    langBtn.addEventListener("click", () =>
      setLang(getLang() === "nl" ? "en" : "nl"),
    );

    // Light/dark is flipped in place on the widget host (no reload:
    // the tokens are CSS variables, every screen re-paints on its own).
    const themeBtn = this.querySelector<HTMLButtonElement>(
      ".scapescene-header__theme",
    )!;
    themeBtn.addEventListener("click", () => {
      const next = getTheme() === "dark" ? "light" : "dark";
      setTheme(next);
      const root = this.getRootNode();
      const host = root instanceof ShadowRoot ? root.host : document.documentElement;
      applyTheme(host, next);
      themeBtn.innerHTML = themeGlyph(next);
    });

    const otherBtn = this.querySelector<HTMLButtonElement>(
      ".scapescene-header__other",
    )!;
    otherBtn.addEventListener("click", () => {
      this.#close();
      router.navigate("/start");
    });
  }

  #toggle(): void {
    this.#open = !this.#open;
    this.#renderOverlayState();
  }

  #close(): void {
    if (!this.#open) return;
    this.#open = false;
    this.#renderOverlayState();
  }

  #renderOverlayState(): void {
    const overlay = this.querySelector<HTMLElement>(".scapescene-header__overlay");
    if (overlay) overlay.hidden = !this.#open;
  }

  #handleOutsideTap(event: Event): void {
    if (!this.#open) return;
    const path = event.composedPath();
    if (!path.includes(this)) {
      this.#close();
    }
  }

  #update(): void {
    const name = store.group?.name || store.group?.projectTitle || store.membership?.groupName || "";
    const code = store.membership?.code ?? store.group?.code ?? "";
    const nameEl = this.querySelector<HTMLElement>(".scapescene-header__chip-name");
    const codeEl = this.querySelector<HTMLElement>(".scapescene-header__chip-code");
    if (nameEl) nameEl.textContent = name || t("untitledGroup");
    if (codeEl) codeEl.textContent = code;

    // Without an active group (the start screen) the bar is brand-only.
    const inGroup = Boolean(store.membership);
    const chip = this.querySelector<HTMLElement>(".scapescene-header__chip");
    const nav = this.querySelector<HTMLElement>(".scapescene-header__nav");
    if (chip) chip.hidden = !inGroup;
    if (nav) nav.hidden = !inGroup;

    const list = this.querySelector<HTMLElement>(".scapescene-header__memberships");
    if (list) {
      list.innerHTML = "";
      for (const membership of store.memberships) {
        const isActive = membership.code === code;
        const row = document.createElement("button");
        row.type = "button";
        row.className = "scapescene-header__membership-row";
        if (isActive) row.classList.add("is-active");
        row.innerHTML = `
          <span class="scapescene-header__membership-name">${escapeHtml(
            membership.groupName || membership.code,
          )}</span>
          <span class="scapescene-header__membership-code">${escapeHtml(
            membership.code,
          )}</span>
        `;
        row.addEventListener("click", () => {
          this.#close();
          if (!isActive) store.switchGroup(membership.code);
        });
        list.appendChild(row);
      }
    }

    this.#updateNav();
  }

  #updateNav(): void {
    const activeName = router.route.name;
    for (const link of this.querySelectorAll<HTMLAnchorElement>(
      ".scapescene-header__nav a",
    )) {
      const route = link.dataset.route;
      link.classList.toggle("is-active", route === activeName);
    }
  }
}

/** Shows the look you would SWITCH TO, like the language button: a sun
 * while dark, a moon while light. */
function themeGlyph(theme = getTheme()): string {
  return theme === "dark"
    ? `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>`
    : `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>`;
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

if (!customElements.get("scapescene-header")) {
  customElements.define("scapescene-header", WorkshopHeader);
}

export {};
